import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import { ToggleRenewalConfirmDialog } from '../TurnOffAutoRenew/ToggleRenewalConfirmDialog';
import { Panel } from '../../../../components/common/Panel';
import { AddOnsView } from './AddOnsView';
import DateTime from '../../../../utils/DateTime';
import { Feature, PlanInterval, Subscription, SubscriptionSource } from '../../../../models';
import { ToggleRenewal } from '../TurnOffAutoRenew/ToggleRenewal';
import { plan } from '../../../../layout/routes';
import { useNavigate, useQuery } from '../../../../routes';
import { fromSubscription, useAppDispatch } from '../../../../store';
import getFeaturePrefix = Feature.getFeaturePrefix;

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 20,
    color: '#22283C',
  },
  contentGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    [theme.breakpoints.down('tablet')]: {
      flexDirection: 'column',
    },
  },
  subscriptionCard: {
    flex: 1,
    border: '1px solid #C4CAEC',
    borderRadius: 8,
    padding: theme.spacing(3),
    backgroundColor: '#fff',
  },
  planName: {
    fontWeight: 700,
    fontSize: 22,
    color: '#22283C',
  },
  seatsLabel: {
    fontWeight: 600,
    fontSize: 16,
    color: '#5959EB',
    marginTop: theme.spacing(0.5),
  },
  addSeatsBtn: {
    marginTop: theme.spacing(2),
    backgroundColor: '#5959EB',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: '#43509B',
    },
  },
  renewalGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
  renewalLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(0.5),
  },
  renewalDate: {
    fontWeight: 700,
    fontSize: 16,
    color: '#22283C',
  },
  renewalDays: {
    fontSize: 13,
    color: '#808B9A',
  },
  billingCycleValue: {
    fontWeight: 600,
    fontSize: 16,
    color: '#22283C',
  },
  autoRenewRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  autoRenewStatus: {
    fontSize: 14,
    fontWeight: 500,
  },
  warningBanner: {
    backgroundColor: '#FFFBE6',
    border: '1px solid #FFD666',
    borderRadius: 6,
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    margin: theme.spacing(0, 2, 2, 2),
  },
  warningText: {
    fontSize: 14,
    color: '#22283C',
  },
  reEnableLink: {
    color: '#5959EB',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
  divider: {
    borderTop: '1px solid #E0E3EF',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
}));

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

export const TrueSubscription = (
  { trueSubscription }: { trueSubscription: Subscription },
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const [autoRenew, setAutoRenew] = useState(
    (trueSubscription?.autoRenewEnabled ?? false) && !trueSubscription?.canceledAt,
  );
  const [reEnableDialogOpen, setReEnableDialogOpen] = useState(false);

  const daysRemaining = trueSubscription.expiryDate
    ? Math.max(
      0,
      Math.floor(
        (new Date(trueSubscription.expiryDate).getTime() - Date.now()) / MILLISECONDS_PER_DAY,
      ),
    )
    : 0;

  const getBillingCycleLabel = () => {
    switch (trueSubscription.billingInterval) {
      case PlanInterval.YEAR:
        return intl.formatMessage({ id: 'page.subscription.billing.interval.year.short' });
      case PlanInterval.MONTH:
        return intl.formatMessage({ id: 'page.subscription.billing.interval.month.short' });
      default:
        return trueSubscription.billingInterval ?? '-';
    }
  };

  const handleAddSeats = () => {
    const prefix = getFeaturePrefix(trueSubscription.feature);
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [`${prefix}Quota`]: 1,
        [`${prefix}Cycle`]: trueSubscription.billingInterval,
      }),
    );
  };

  const handleReEnableAutoRenew = () => {
    if (!trueSubscription.recurlySubscriptionId) return;
    dispatch(fromSubscription.doToggleAutoRenew(trueSubscription.recurlySubscriptionId))
      .then(unwrapResult)
      .then(renewed => {
        setAutoRenew(renewed);
        setReEnableDialogOpen(false);
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'page.subscription.toggle.auto.renew.error' },
            { action: 'turn on' },
          ),
          { variant: 'error' },
        );
      });
  };

  return (
    <>
      <Panel
        id={`${trueSubscription.feature}-upsell-panel`}
        sx={{
          width: '100%',
          minWidth: 600,
        }}
        border
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          padding={theme => theme.spacing(4, 4, 2, 4)}
          gap={2}
        >
          {/* Left: plan name + seats + add seats */}
          <Box>
            <Typography className={classes.planName}>
              {trueSubscription.feature === Feature.TRUE_TEAM ? 'True Team' : 'True Enterprise'}
            </Typography>
            <Typography className={classes.seatsLabel}>
              <FormattedMessage
                id="page.subscription.v2.seats.count"
                values={{ count: trueSubscription.quota }}
              />
            </Typography>
            {trueSubscription.source === SubscriptionSource.RECURLY && (
              <Button
                variant="contained"
                size="small"
                className={classes.addSeatsBtn}
                onClick={handleAddSeats}
                sx={{
                  borderRadius: 2,
                }}
              >
                <FormattedMessage id="page.subscription.v2.add.seats.btn" />
              </Button>
            )}
          </Box>

          {/* Right: renewal / auto-renew / billing cycle */}
          <Box className={classes.renewalGrid}>
            <Box
              display="flex"
              flexDirection="column"
            >
              <Typography className={classes.renewalLabel}>
                <FormattedMessage id="page.subscription.v2.next.renewal.label" />
              </Typography>
              <Typography className={classes.renewalDate}>
                {DateTime.formatDate(intl, trueSubscription.expiryDate)}
              </Typography>
              <Typography className={classes.renewalDays}>
                <FormattedMessage
                  id="page.subscription.v2.days.remaining"
                  values={{ days: daysRemaining }}
                />
              </Typography>
            </Box>

            <Divider
              id={`${trueSubscription.feature}-upsell-panel-divider`}
              orientation="vertical"
              sx={theme => ({
                width: '1px',
                backgroundColor: theme.common.grey[700],
                flexDirection: 'column',
              })}
              flexItem
            />

            <Box>
              <Box>
                <Typography className={classes.renewalLabel}>
                  <FormattedMessage id="page.subscription.v2.billing.cycle.label" />
                </Typography>
                <Typography className={classes.billingCycleValue}>
                  {getBillingCycleLabel()}
                </Typography>
              </Box>
              <Box
                sx={{ marginTop: 2 }}
              >
                <Typography className={classes.renewalLabel}>
                  <FormattedMessage id="page.subscription.v2.auto.renew.label" />
                </Typography>
                <Box className={classes.autoRenewRow}>
                  {trueSubscription.source === SubscriptionSource.RECURLY
                    && trueSubscription.recurlySubscriptionId && (
                      <ToggleRenewal
                        productName={trueSubscription.feature.toLowerCase()}
                        autoRenewal={autoRenew}
                        recurlySubscriptionId={trueSubscription.recurlySubscriptionId}
                        setAutoRenew={setAutoRenew}
                      />
                  )}
                  <Typography
                    className={classes.autoRenewStatus}
                    sx={{ color: autoRenew ? '#17B26A' : '#808B9A' }}
                  >
                    {autoRenew
                      ? intl.formatMessage({ id: 'page.subscription.v2.auto.renew.on' })
                      : intl.formatMessage({ id: 'page.subscription.v2.auto.renew.off' })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Auto-renew off warning banner */}
        {!autoRenew && trueSubscription.source === SubscriptionSource.RECURLY && (
          <Box className={classes.warningBanner}>
            <WarningAmberIcon sx={{ color: '#B7652A', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
            <Typography className={classes.warningText}>
              <strong style={{ color: '#B7652A' }}>
                <FormattedMessage id="page.subscription.v2.auto.renew.off.warning.title" />
              </strong>
            </Typography>
            <Typography className={classes.warningText}>
              <FormattedMessage
                id="page.subscription.v2.auto.renew.off.warning.desc"
                values={{
                  date: (
                    <strong>{DateTime.formatDate(intl, trueSubscription.expiryDate)}</strong>
                  ),
                }}
              />
              <span
                className={classes.reEnableLink}
                role="button"
                tabIndex={0}
                onClick={() => setReEnableDialogOpen(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setReEnableDialogOpen(true);
                }}
              >
                <FormattedMessage id="page.subscription.v2.auto.renew.off.warning.reenable" />
              </span>
            </Typography>
          </Box>
        )}

        {/* Add-ons */}
        <Box className={classes.divider} />
        <AddOnsView
          key="addons-view"
          trueSubscription={trueSubscription}
        />
      </Panel>
      {/* Re-enable renewal confirm dialog */}
      {trueSubscription.recurlySubscriptionId && (
        <ToggleRenewalConfirmDialog
          productName={trueSubscription.feature.toLowerCase()}
          autoRenew={autoRenew}
          open={reEnableDialogOpen}
          onClose={() => setReEnableDialogOpen(false)}
          toggleAutoRenew={handleReEnableAutoRenew}
        />
      )}
    </>
  );
};
